import react, { useReducer, useState } from 'react'
import {
    Popper, Box, ClickAwayListener, Paper, List, ListItemButton, ListItemText,
    Checkbox, Button, IconButton, TextField, ListItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'

import api from '../../api';
import { getSession } from '../../auth';
import { colorOptionsMedium } from "./index"
import { lockedReducer } from '../../utils/defaultReducer';
import { dataSource } from '../../utils/emojiDict';
import ConfirmationDialogCaptcha from '../Dialogs/ConfimationDialogCaptcha';

const initialState = {
    id: 0,
    name: '',
    color: '',
    edit: false,
    remove: false
}

const unifyData = ({ data = [], unifier = 'id' }) => {
    return Object.values(Object.fromEntries(data.map(i => ([i[unifier], i]))))
}

const OpportunityMarkers = ({ tagsOpen, setTagsOpen, allTags, setAllTags, setParent, parent, newTagsRef, selectedJourney }) => {

    const [state, setState] = useReducer(lockedReducer, initialState)
    const [searchTerm, setSearchTerm] = useState('')

    const filteredTags = searchTerm.trim()
        ? allTags.filter((tag) => tag.name.toLowerCase().includes(searchTerm.toLowerCase()))
        : allTags

    const removeTag = async () => {
        const enterpriseId = getSession().profile.enterprise.id

        const response = await api.delete(`api/enterprises/${enterpriseId}/journeys/${selectedJourney.id}/tags/${state.id}/`)

        if (response.status === 204) {
            if (setAllTags) setAllTags((prevState) => (prevState.filter(tag => tag.id !== state.id)));
            setParent((prevState) => {
                return {
                    ...prevState,
                    tags: ((prevState.tags || []).filter(tag => tag !== state.id)),
                    allTags: ((parent.allTags || []).filter(tag => tag.id !== state.id))
                }
            })
            setState(initialState)
        }
    }

    const updateTag = async () => {
        const enterpriseId = getSession().profile.enterprise.id
        let response = null
        if (state.id) {
            response = await api.patch(`api/enterprises/${enterpriseId}/journeys/${selectedJourney.id}/tags/${state.id}/`, {
                name: state.name,
                color: state.color
            })
        }
        else {
            response = await api.post(`api/enterprises/${enterpriseId}/journeys/${selectedJourney.id}/tags/`, {
                name: state.name,
                color: state.color
            })
        }


        if ([200, 201].includes(response.status)) {
            if (setAllTags) setAllTags((prevState) => (unifyData({ data: [...prevState, response.data] })));
            setParent((prevState) => {
                const currentTags = prevState.tags || []
                const updateTags = response.status === 200 ? currentTags : Array.from(new Set([...currentTags, response.data.id]))
                return {
                    ...prevState,
                    tags: updateTags,
                    allTags: unifyData({ data: [...(parent.allTags || []), response.data] })
                }
            })
            setState(initialState)
        }
    }

    return (
        <>
            <Popper
                open={tagsOpen}
                anchorEl={newTagsRef.current}
                placement="top-start"
                sx={{ zIndex: 1300 }}
            >
                <ClickAwayListener onClickAway={() => { setTagsOpen(false); setSearchTerm('') }}>
                    <Paper>
                        {!state.edit && (
                            <Box className="opportunity-tags-container">
                                <TextField
                                    size="small"
                                    placeholder="Buscar marcador..."
                                    fullWidth
                                    value={searchTerm}
                                    onChange={({ target }) => setSearchTerm(target.value)}
                                    sx={{ mb: 1 }}
                                />
                                {filteredTags.length === 0 && (
                                    <Box sx={{ padding: "1rem" }}>
                                        {searchTerm.trim() ? 'Nenhum marcador encontrado.' : 'Jornada sem marcadores.'}
                                    </Box>
                                )}
                                {filteredTags.length > 0 && (
                                    <List sx={{ margin: 0, overflow: 'auto', maxHeight: '20rem' }} dense>
                                        {filteredTags.map((tag) => {
                                            const isSelected = parent.tags?.includes(
                                                tag.id
                                            );
                                            return (
                                                <ListItem disableGutters disablePadding>
                                                    <ListItemButton
                                                        key={`op-tag-${tag.name}`}
                                                        onClick={() =>
                                                            setParent((prevState) => {
                                                                const tags = prevState.tags || [];
                                                                return {
                                                                    ...prevState,
                                                                    tags: isSelected
                                                                        ? tags.filter(
                                                                            (id) => id !== tag.id
                                                                        )
                                                                        : [...tags, tag.id],
                                                                };
                                                            })
                                                        }
                                                    >
                                                        <Checkbox
                                                            checked={isSelected}
                                                            edge="start"
                                                            tabIndex={-1}
                                                        />
                                                        <ListItemText
                                                            primary={tag.name}
                                                            primaryTypographyProps={{
                                                                sx: { backgroundColor: tag.color },
                                                            }}
                                                        />
                                                    </ListItemButton>
                                                    <IconButton sx={{ padding: 0.5 }} onClick={() => setState({ ...tag, edit: true })}><EditIcon /></IconButton>
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                )}
                                <Button
                                    className="opportunity-btn"
                                    onClick={() => { setSearchTerm(''); setState({ ...initialState, edit: true }) }}
                                >
                                    <AddIcon />
                                    Criar marcador
                                </Button>
                            </Box>
                        )}
                        {state.edit && (
                            <Box className="new-tag-container">
                                <Box className="new-tag-container-header">
                                    <IconButton
                                        size="small"
                                        onClick={() => { setSearchTerm(''); setState(initialState) }}
                                    >
                                        <ArrowBackIcon />
                                    </IconButton>
                                    {state.id ? 'Editar ' : 'Novo '} Marcador
                                </Box>
                                <TextField
                                    size="small"
                                    placeholder="Nome"
                                    fullWidth
                                    value={state.name}
                                    onChange={({ target }) =>
                                        setState({
                                            name: target.value,
                                        })
                                    }
                                    inputProps={{ maxLength: 32 }}
                                />
                                <Box className="color-selector">
                                    {colorOptionsMedium.map((color) => {
                                        const isSelected =
                                            state.color === color.value;
                                        return (
                                            <Box
                                                onClick={() =>
                                                    setState({ color: color.value })
                                                }
                                                sx={{
                                                    width: 30,
                                                    height: 30,
                                                    bgcolor: color.value,
                                                    borderRadius: "4px",
                                                    cursor: "pointer",
                                                    border: isSelected
                                                        ? "2px solid #000"
                                                        : "1px solid #ccc",
                                                    transition: "border-color 0.3s",
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                                <Box display='flex' gap='.25rem' width='100%'>
                                    <Button
                                        sx={{ flex: 1 }}
                                        className="opportunity-btn"
                                        onClick={() => updateTag()}
                                        disabled={!state.name || !state.color}
                                    >
                                        <SaveIcon />
                                        Salvar
                                    </Button>
                                    {state.id ? <Button
                                        color='error'
                                        sx={{ flex: 1 }}
                                        className="opportunity-btn delete"
                                        onClick={() => setState({ remove: true })}
                                    >
                                        <DeleteIcon />
                                        Excluir
                                    </Button> : null}
                                </Box>
                            </Box>
                        )}
                        {state.remove && <ConfirmationDialogCaptcha
                            title='Excluir marcador'
                            description={<>Deseja excluir permanentemente o marcador {state.name}? Isso irá removê-lo de todas as oportunidades no sistema.
                                Para confirmar, digite <span style={{ fontWeight: 'bold' }}>remover</span> no campo abaixo.</>}
                            captcha='remover'
                            open={true}
                            handleClose={() => setState({ remove: false })}
                            handleSubmit={removeTag}
                        />}
                    </Paper>

                </ClickAwayListener>
            </Popper>
        </>
    )
}

export default OpportunityMarkers
